<script setup>
import { computed } from 'vue';
import { useSidebarContext } from './provider';
import Icon from 'next/icon/Icon.vue';
import Logo from 'next/icon/Logo.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';

const props = defineProps({
  items: { type: Array, required: true },
  bottomItemNames: { type: Array, default: () => [] },
  activeSectionName: { type: String, default: '' },
  badges: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['openKeyShortcutModal']);

const { isAllowed } = useSidebarContext();

const leavesOf = item =>
  item.children ? item.children.flatMap(leavesOf) : [item];

// A section opens on its own route, or on the first child the user can reach.
const toRailItem = item => {
  const linkTo = item.to?.name
    ? item.to
    : leavesOf(item).find(leaf => leaf.to && isAllowed(leaf.to))?.to;
  return {
    ...item,
    linkTo,
    isActive: item.name === props.activeSectionName,
    badge: props.badges[item.name] || 0,
  };
};

const visibleItems = computed(() =>
  props.items
    .map(toRailItem)
    .filter(item => item.linkTo && isAllowed(item.linkTo))
);

// Bottom items sit at the foot of the rail, above the profile menu.
const railItems = computed(() => {
  const top = visibleItems.value.filter(
    item => !props.bottomItemNames.includes(item.name)
  );
  const bottom = props.bottomItemNames
    .map(name => visibleItems.value.find(item => item.name === name))
    .filter(Boolean)
    .map((item, index) => ({ ...item, pushesDown: index === 0 }));
  return [...top, ...bottom];
});
</script>

<template>
  <div
    class="flex flex-col items-center flex-shrink-0 w-14 h-full py-3 gap-1 bg-n-rail"
  >
    <div class="grid mb-3 size-9 place-content-center">
      <Logo class="rounded-lg size-8" />
    </div>
    <router-link
      v-for="item in railItems"
      :key="item.name"
      v-tooltip.right="item.label"
      :to="item.linkTo"
      class="relative grid transition-colors duration-150 size-10 place-content-center rounded-xl"
      :class="[
        item.isActive
          ? 'bg-n-brand text-white'
          : 'text-white/60 hover:text-white hover:bg-white/10',
        { 'mt-auto': item.pushesDown },
      ]"
      :aria-label="item.label"
    >
      <Icon :icon="item.icon" class="size-5" />
      <span
        v-if="item.badge"
        class="absolute top-1 grid h-4 px-1 font-medium text-white rounded-full min-w-4 ltr:right-1 rtl:left-1 place-content-center text-xxs bg-n-brand ring-2 ring-n-rail"
      >
        {{ Math.min(item.badge, 99) }}
      </span>
    </router-link>
    <div class="mt-2">
      <SidebarProfileMenu
        is-collapsed
        @open-key-shortcut-modal="emit('openKeyShortcutModal')"
      />
    </div>
  </div>
</template>
